/**
 * ChatInbox — shared inbox page for both buyer (/messages) and seller
 * (/workspace/inbox). Left column: threads list. Right column: the
 * active thread's DirectChatWindow.
 */
import * as React from "react";
import { useSearchParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { MessageCircle, Package, Store, User as UserIcon } from "lucide-react";
import { api } from "@/lib/api";
import { DirectChatWindow } from "@/components/direct_chat/DirectChatWindow";
import { PushNotificationsCard } from "@/components/push/PushNotificationsCard";
import { SellerResponseBadge } from "@/components/direct_chat/SellerResponseBadge";

interface ThreadRow {
  thread_id: string;
  buyer_name?: string;
  seller_workspace_name?: string;
  product?: { title?: string; image_url?: string; slug?: string };
  last_message_at?: string;
  last_message_preview?: string;
  last_sender_role?: string;
  unread?: number;
  closed?: boolean;
}

export const ChatInbox: React.FC<{ role: "buyer" | "seller" }> = ({ role }) => {
  const [threads, setThreads] = React.useState<ThreadRow[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [params, setParams] = useSearchParams();
  const activeId = params.get("thread");

  // Refresh unread badges site-wide (the global notifier owns the polling
  // loop — we just poke it after read events).
  const refreshUnread = React.useCallback(() => {
    api.get("/direct-chat/unread").catch(() => {});
  }, []);

  const load = React.useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get(`/direct-chat/threads/mine?role=${role}`);
      setThreads(r.data?.threads || []);
    } catch {
      setThreads([]);
    } finally {
      setLoading(false);
    }
  }, [role]);

  React.useEffect(() => { load(); }, [load]);

  // Refresh the thread list every 15 s so the seller sees new incoming
  // rows without needing to reload the page.
  React.useEffect(() => {
    const t = window.setInterval(load, 15_000);
    return () => window.clearInterval(t);
  }, [load]);

  return (
    <div className="min-h-screen bg-neutral-50 dark:bg-slate-950 text-neutral-900 dark:text-white">
      <header className="border-b border-neutral-200 dark:border-white/10 bg-white/70 dark:bg-slate-950/70 backdrop-blur px-4 sm:px-6 py-4">
        <div className="max-w-6xl mx-auto flex items-center gap-3">
          <div className="h-9 w-9 rounded-full bg-gradient-to-br from-orange-500 to-fuchsia-500 grid place-items-center text-white">
            <MessageCircle className="h-4 w-4" />
          </div>
          <div>
            <div className="font-display font-bold text-lg leading-tight" data-testid="chat-inbox-title">
              {role === "buyer" ? "Messages" : "Seller Inbox"}
            </div>
            <div className="text-[11px] text-neutral-500 dark:text-slate-400">
              {role === "buyer"
                ? "Your direct chats with sellers about specific products"
                : "Buyers asking about your listings — reply fast for higher conversions"}
            </div>
          </div>
          {role === "seller" && (
            <div className="ml-auto">
              <SellerResponseBadge size="md" showHint />
            </div>
          )}
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6 grid grid-cols-1 lg:grid-cols-12 gap-4">
        <aside className="lg:col-span-4 xl:col-span-3 space-y-2" data-testid="chat-inbox-thread-column">
          <PushNotificationsCard compact />
          {loading && (
            <div className="space-y-2" data-testid="chat-inbox-skeleton">
              {[0, 1, 2].map((i) => (
                <div key={i} className="rounded-xl bg-white dark:bg-slate-900 border border-neutral-200 dark:border-white/10 p-3 flex gap-3 animate-pulse">
                  <div className="h-11 w-11 rounded-lg bg-neutral-200 dark:bg-white/10 shrink-0" />
                  <div className="flex-1 space-y-2">
                    <div className="h-3 w-2/3 bg-neutral-200 dark:bg-white/10 rounded" />
                    <div className="h-2.5 w-1/2 bg-neutral-100 dark:bg-white/5 rounded" />
                    <div className="h-2.5 w-4/5 bg-neutral-100 dark:bg-white/5 rounded" />
                  </div>
                </div>
              ))}
            </div>
          )}
          {!loading && threads.length === 0 && (
            <div className="rounded-xl bg-white dark:bg-slate-900 border border-neutral-200 dark:border-white/10 p-6 text-center text-sm text-neutral-500 dark:text-slate-400">
              {role === "buyer"
                ? <>No conversations yet.<br/>Ask Sancta to "talk to the seller" on any product to start one.</>
                : <>No incoming chats yet — as soon as a buyer messages you here, you'll get an alert.</>
              }
            </div>
          )}
          {threads.map((t) => {
            const active = activeId === t.thread_id;
            const otherLabel = role === "buyer"
              ? (t.seller_workspace_name || "Seller")
              : (t.buyer_name || "Buyer");
            return (
              <button
                key={t.thread_id}
                data-testid={`chat-inbox-thread-${t.thread_id}`}
                onClick={() => { setParams({ thread: t.thread_id }); refreshUnread(); }}
                className={`w-full text-left rounded-xl border transition-colors p-3 flex gap-3 ${
                  active
                    ? "border-orange-400 bg-orange-50 dark:border-orange-500/60 dark:bg-orange-500/10"
                    : "border-neutral-200 dark:border-white/10 bg-white dark:bg-slate-900 hover:border-neutral-300 dark:hover:border-white/20"
                }`}
              >
                {t.product?.image_url ? (
                  <img src={t.product.image_url} alt="" loading="lazy" decoding="async" className="h-11 w-11 rounded-lg object-cover border border-neutral-200 dark:border-white/10 shrink-0" />
                ) : (
                  <div className="h-11 w-11 rounded-lg bg-neutral-200 dark:bg-white/10 grid place-items-center shrink-0">
                    {role === "buyer" ? <Store className="h-4 w-4" /> : <UserIcon className="h-4 w-4" />}
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <div className="flex items-baseline justify-between gap-2">
                    <div className="text-sm font-semibold truncate">{otherLabel}</div>
                    {(t.unread ?? 0) > 0 && (
                      <span className="shrink-0 text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-fuchsia-500 text-white" data-testid={`chat-inbox-thread-unread-${t.thread_id}`}>
                        {t.unread}
                      </span>
                    )}
                  </div>
                  <div className="text-[11px] text-neutral-500 dark:text-slate-400 truncate flex items-center gap-1">
                    <Package className="h-3 w-3" /> {t.product?.title || "General"}
                  </div>
                  {t.last_message_preview && (
                    <div className="text-[12px] text-neutral-600 dark:text-slate-300 truncate pt-1">
                      {t.last_sender_role === role ? "You: " : ""}{t.last_message_preview}
                    </div>
                  )}
                </div>
              </button>
            );
          })}
        </aside>

        <main className="lg:col-span-8 xl:col-span-9">
          {activeId ? (
            <motion.div
              key={activeId}
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.2 }}
              className="h-[calc(100vh-180px)] min-h-[500px]"
            >
              <DirectChatWindow threadId={activeId} onRead={() => { load(); refreshUnread(); }} />
            </motion.div>
          ) : (
            <div className="h-[calc(100vh-180px)] min-h-[500px] rounded-2xl bg-white dark:bg-slate-900 border border-neutral-200 dark:border-white/10 grid place-items-center text-center px-6" data-testid="chat-inbox-empty">
              <div className="max-w-sm space-y-2">
                <div className="h-14 w-14 mx-auto rounded-2xl bg-gradient-to-br from-orange-500/20 to-fuchsia-500/20 grid place-items-center text-orange-500">
                  <MessageCircle className="h-6 w-6" />
                </div>
                <div className="font-display text-lg font-semibold">
                  {role === "buyer" ? "Pick a conversation" : "Your seller inbox is ready"}
                </div>
                <div className="text-sm text-neutral-500 dark:text-slate-400">
                  {role === "buyer"
                    ? <>Select a thread on the left, or head to <Link className="text-fuchsia-500 hover:underline" to="/shop">/shop</Link> and ask Sancta to talk to the seller.</>
                    : <>You'll hear a soft chime the moment a buyer messages you. Reply within 15 minutes to boost your response-time score.</>
                  }
                </div>
              </div>
            </div>
          )}
        </main>
      </div>
    </div>
  );
};

export default ChatInbox;
